import { useEffect, useRef, useState } from 'react';

import { useRouter } from '@/hooks/useRouter';
import {
  trackRideEntryPointViewedEvent,
  TRideEntryPointViewedEvent,
} from '@/services/analytics/ride';

// Half on screen, so a sliver peeking in at the viewport edge doesn't count as seen.
// An element taller than twice the viewport can never reach this, so it would never fire.
const VISIBLE_RATIO = 0.5;

/** Once per page path, not per mount: the global header persists across client-side navigations. */
export const useRideEntryPointImpression = (event: TRideEntryPointViewedEvent | null) => {
  const { asPath } = useRouter();
  const pagePath = (asPath || '').split(/[?#]/)[0] ?? '';

  const [node, setNode] = useState<Element | null>(null);
  const hasFiredRef = useRef(false);

  // Read at fire time so a destination that settles after mount doesn't restart the observer.
  const eventRef = useRef(event);
  eventRef.current = event;
  const isEnabled = event !== null;

  // Declared before the observer effect so a new path is reset before it's observed.
  useEffect(() => {
    hasFiredRef.current = false;
  }, [pagePath]);

  useEffect(() => {
    if (!node || !isEnabled || hasFiredRef.current) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // The initial callback reports any overlap, so check the ratio itself.
        if (!entry || entry.intersectionRatio < VISIBLE_RATIO || !eventRef.current) return;

        trackRideEntryPointViewedEvent(eventRef.current);
        hasFiredRef.current = true;
        observer.disconnect();
      },
      { threshold: VISIBLE_RATIO },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [node, isEnabled, pagePath]);

  return setNode;
};
